// src/components/Modals/CreateEditAuditModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  Container,
  Card,
  CardContent,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon              from "@mui/icons-material/Close";
import EventNoteIcon          from "@mui/icons-material/EventNote";
import LocationOnIcon         from "@mui/icons-material/LocationOn";
import PersonIcon             from "@mui/icons-material/Person";
import InfoOutlinedIcon       from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";
import LockIcon               from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";

import { useAuth } from "../../contexts/AuthContext";
import {
  fetchLocations,
  fetchAuditors,
  fetchAssetsByLocation,
  createAuditEvent,
  updateAuditEvent,
} from "../../services/auditService";

// ─── Slide Transition ─────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Status options ───────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "initiated",   label: "Initiated",   color: "#1d4ed8", bg: "#eff6ff" },
  { value: "in_progress", label: "In Progress", color: "#1d4ed8", bg: "#eff6ff" },
  { value: "completed",   label: "Completed",   color: "#16a34a", bg: "#f0fdf4" },
];

// ─── Initial form state ───────────────────────────────────────────────────────
// NOTE: `location_id` is an internal UI-only field that tracks the selected
//       dropdown value (the location record's primary key).  It is never sent
//       to the database — the payload uses `locationCode` instead.

const INIT = {
  title:          "",
  description:    "",
  location_id:    "",
  auditor_id:     "",
  scheduled_date: "",
  status:         "initiated",
};

const auditToForm = (audit) => ({
  title:          audit.title          ?? "",
  description:    audit.description    ?? "",
  location_id:    "",  // resolved after locations load via locationCode
  auditor_id:     audit.auditor_id     ?? "",
  scheduled_date: audit.scheduled_date ?? "",
  status:         audit.status         ?? "initiated",
});

// ─── Field ID map (for auto-scroll targeting) ────────────────────────────────
const FIELD_ID_MAP = {
  title:          "audit-title",
  scheduled_date: "audit-date",
  location_id:    "audit-location",
  auditor_id:     "audit-auditor",
};

const _log = (...args) => {
  if (process.env.NODE_ENV !== "production")
    console.log("[CreateEditAuditModal]", ...args);
};

// ─── Name resolver helper (UI display only — never written to DB) ────────────

const resolveUserName = (user) => {
  if (!user) return "Unknown User";
  const candidates = [
    user.displayName,
    user.name,
    user.fullName,
    user.full_name,
    user.username,
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || null,
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() || null,
  ];
  for (const c of candidates) {
    if (c && typeof c === "string" && c.trim()) return c.trim();
  }
  if (user.email && typeof user.email === "string") {
    return user.email.split("@")[0];
  }
  return "Unknown User";
};

// ═════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

const FormGroup = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasError",
})(({ hasError }) => ({
  flex: 1,
  minWidth: "200px",
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset": { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset": { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow: hasError
        ? "0 0 0 3px rgba(220,38,38,0.12)"
        : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
}));

const FormRow = styled(Box)(() => ({
  display: "flex",
  gap: "1rem",
  marginBottom: "1rem",
  flexWrap: "wrap",
  "&:last-child": { marginBottom: 0 },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
}));

const RequiredMark = styled("span")(() => ({
  color: "#ef4444",
  marginLeft: "0.125rem",
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color: "#dc2626",
  fontSize: "0.75rem",
  fontWeight: 500,
  margin: 0,
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
}));

// ─── SX constants ─────────────────────────────────────────────────────────────

const cancelBtnSx = {
  px: 3,
  py: 1,
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3,
  py: 1,
  backgroundColor: "#2563eb",
  color: "#fff",
  borderRadius: "6px",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "160px",
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&:hover": { backgroundColor: "#1d4ed8" },
  "&:disabled": { backgroundColor: "#93c5fd" },
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const CreateEditAuditModal = ({
  open,
  onClose,
  onCreated,
  onUpdated,
  auditToEdit = null,
}) => {
  const { currentUser } = useAuth();
  const mode = auditToEdit ? "edit" : "create";

  // ── Form state ─────────────────────────────────────────────────────────────
  const [form,        setForm]        = useState(INIT);
  const [errors,      setErrors]      = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting,  setSubmitting]  = useState(false);

  // ── Data state ────────────────────────────────────────────────────────────
  const [locations,   setLocations]   = useState([]);
  const [auditors,    setAuditors]    = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const initialized      = useRef(false);
  const locationResolved = useRef(false);
  const formBodyRef      = useRef(null);

  // ── REVISED LOCK POLICY ────────────────────────────────────────────────────
  //
  //   auditorIsLocked   → auditor field is READ-ONLY. Only true when the
  //                       audit has been fully completed and finalized.
  //
  //   isReassignmentInProgress → user is picking a different auditor while
  //                              the audit is already "in_progress". Shows
  //                              an informational banner explaining that
  //                              already-audited assets keep their original
  //                              auditor snapshot.
  //
  // Policy summary:
  //   - initiated   → CAN change auditor (no warning)
  //   - in_progress → CAN change auditor (info banner shown when changed)
  //   - completed   → CANNOT change auditor (locked)
  //
  const auditorIsLocked =
    mode === "edit" && auditToEdit?.status === "completed";

  const isReassignmentInProgress =
    mode === "edit" &&
    auditToEdit?.status === "in_progress" &&
    Boolean(form.auditor_id) &&
    form.auditor_id !== (auditToEdit?.auditor_id ?? "");

  // ═════════════════════════════════════════════════════════════════
  // SCROLL LOCK
  // ═════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!open) return undefined;

    const html = document.documentElement;
    const previousOverflow     = html.style.overflow;
    const previousPaddingRight = html.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      html.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      html.style.overflow     = previousOverflow;
      html.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  // ── Scroll-to-field helper ────────────────────────────────────────────────
  const scrollToField = useCallback((fieldName) => {
    const domId = FIELD_ID_MAP[fieldName] || fieldName;
    const el = document.getElementById(domId);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    setTimeout(() => {
      try {
        if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
          el.focus();
        } else {
          const focusable = el.querySelector("input, textarea, [tabindex]");
          if (focusable) focusable.focus();
          else el.focus?.();
        }
      } catch (e) {
        /* noop */
      }
    }, 450);
  }, []);

  // ── Load locations + auditors ─────────────────────────────────────────────
  const loadMeta = useCallback(async () => {
    if (!currentUser?.companyId) return;
    setLoadingMeta(true);
    try {
      const [locs, users] = await Promise.all([
        fetchLocations(currentUser.companyId),
        fetchAuditors(currentUser.companyId),
      ]);
      setLocations(locs);
      setAuditors(users);
    } catch (err) {
      console.error("[CreateEditAuditModal] loadMeta:", err);
    } finally {
      setLoadingMeta(false);
    }
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (!open) {
      initialized.current      = false;
      locationResolved.current = false;
      return;
    }
    if (initialized.current) return;

    initialized.current = true;
    setErrors({});
    setSubmitError("");
    setForm(auditToEdit ? auditToForm(auditToEdit) : INIT);
    loadMeta();
  }, [open, auditToEdit, loadMeta]);

  // ── Resolve location dropdown from auditToEdit.locationCode ───────────────
  // The audit event model stores `locationCode`, but the dropdown tracks the
  // location record's primary key (`loc.id`).  Once the locations list is
  // loaded we find the matching record and set the dropdown value.
  useEffect(() => {
    if (mode !== "edit") return;
    if (!auditToEdit?.locationCode) return;
    if (locations.length === 0) return;
    if (locationResolved.current) return;

    const match = locations.find(
      (l) => l.locationCode === auditToEdit.locationCode,
    );
    if (match) {
      locationResolved.current = true;
      setForm((prev) => ({ ...prev, location_id: match.id }));
    }
  }, [locations, auditToEdit, mode]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSubmitError("");
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const e = {};
    if (!form.title.trim())   e.title          = "Audit title is required";
    if (!form.location_id)    e.location_id    = "Please select a location";
    if (!form.auditor_id)     e.auditor_id     = "Please assign an auditor";
    if (!form.scheduled_date) e.scheduled_date = "Scheduled date is required";
    setErrors(e);

    const errorKeys = Object.keys(e);
    if (errorKeys.length > 0) {
      const count = errorKeys.length;
      setSubmitError(
        `Please fix ${count} ${count === 1 ? "error" : "errors"} highlighted below before submitting.`,
      );
      setTimeout(() => scrollToField(errorKeys[0]), 100);
    }

    return errorKeys.length === 0;
  }, [form, scrollToField]);

  // ── handleCreate ──────────────────────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    const companyId    = currentUser?.companyId;
    const userId       = currentUser?.uid ?? currentUser?.id ?? "";
    const selLocation  = locations.find((l) => l.id === form.location_id);
    const selAuditor   = auditors.find((u) => u.id === form.auditor_id);
    const locationCode = selLocation?.locationCode ?? "";

    _log("submit → fetching assets for", form.location_id);

    const assetList = await fetchAssetsByLocation(
      form.location_id,
      companyId,
      locationCode,
    );

    if (!assetList.length) {
      throw new Error(
        "No assets found at this location. Add assets to this branch first.",
      );
    }

    // Payload uses ONLY AuditEventModel.ALLOWED_KEYS.
    // Metadata (createdAt, createdBy, etc.) is stamped inside auditService.
    const payload = {
      companyId,
      locationCode,
      auditor_id:     form.auditor_id,
      auditor_email:  selAuditor?.email ?? "",
      title:          form.title.trim(),
      description:    form.description.trim(),
      scheduled_date: form.scheduled_date,
      status:         "initiated",
    };

    const newEvent = await createAuditEvent(payload, assetList, userId);
    onCreated?.(newEvent);
  }, [form, locations, auditors, currentUser, onCreated]);

  // ── handleUpdate ──────────────────────────────────────────────────────────
  const handleUpdate = useCallback(async () => {
    const companyId    = currentUser?.companyId;
    const userId       = currentUser?.uid ?? currentUser?.id ?? "";
    const selLocation  = locations.find((l) => l.id === form.location_id);
    const selAuditor   = auditors.find((u) => u.id === form.auditor_id);
    const locationCode = selLocation?.locationCode ?? "";

    const locationChanged =
      locationCode !== (auditToEdit?.locationCode ?? "");

    let assetList = null;
    if (locationChanged) {
      _log("edit → location changed, fetching new assets");
      assetList = await fetchAssetsByLocation(
        form.location_id,
        companyId,
        locationCode,
      );
      if (!assetList.length) {
        throw new Error(
          "No assets found at the new location. Cannot switch to an empty location.",
        );
      }
    }

    // Payload uses ONLY AuditEventModel.ALLOWED_KEYS.
    // When audit is LOCKED (completed), omit auditor fields entirely so no
    // accidental overwrite can happen.  Metadata (updatedAt, updatedBy) is
    // stamped inside auditService.
    const payload = {
      locationCode,
      title:          form.title.trim(),
      description:    form.description.trim(),
      scheduled_date: form.scheduled_date,
      status:         form.status,
      ...(!auditorIsLocked && {
        auditor_id:    form.auditor_id,
        auditor_email: selAuditor?.email ?? "",
      }),
    };

    const updated = await updateAuditEvent(
      auditToEdit.id,
      payload,
      assetList,
      userId,
    );

    onUpdated?.(updated);
  }, [form, locations, auditors, currentUser, auditToEdit, auditorIsLocked, onUpdated]);

  // ── Master submit ──────────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      if (!validate()) return;

      setSubmitting(true);
      setSubmitError("");

      try {
        if (mode === "edit") await handleUpdate();
        else await handleCreate();
        onClose();
      } catch (err) {
        console.error("[CreateEditAuditModal] submit:", err);
        setSubmitError(
          err.message || "Something went wrong. Please try again.",
        );
        if (formBodyRef.current) {
          formBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [validate, mode, handleCreate, handleUpdate, onClose],
  );

  // ── Derived ────────────────────────────────────────────────────────────────
  const today       = new Date().toISOString().split("T")[0];
  const selAuditor  = auditors.find((u) => u.id === form.auditor_id) ?? null;
  const selLocation = locations.find((l) => l.id === form.location_id) ?? null;
  const selStatus   = STATUS_OPTIONS.find((s) => s.value === form.status);

  const locationChanged =
    mode === "edit" &&
    Boolean(form.location_id) &&
    (selLocation?.locationCode ?? "") !== (auditToEdit?.locationCode ?? "");

  const isSubmitDisabled = submitting || loadingMeta;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!submitting ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* ── FULL-SCREEN APP BAR ── */}
      <AppBar
        sx={{
          position: "relative",
          flexShrink: 0,
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {mode === "edit" ? "Edit Audit Event" : "Create New Audit Event"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {mode === "edit"
                ? `Editing: ${auditToEdit?.title ?? ""}`
                : "Assign a location, auditor and scheduled date"}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── FLEX SHELL: scrollable body + sticky footer ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f1f5f9",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Scrollable form body */}
        <Box
          component="form"
          id="audit-form"
          ref={formBodyRef}
          onSubmit={handleSubmit}
          noValidate
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 4,
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3}>

              {/* Edit Mode Warning Banner */}
              {mode === "edit" && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  You are editing an existing audit. Changing the location will
                  reload assets and replace the existing audit items when you
                  save.
                </Alert>
              )}

              {/* ===== SECTION 1: EVENT DETAILS ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <EventNoteIcon color="primary" fontSize="small" />
                    <Typography
                      sx={{
                        fontSize: "0.9375rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      Audit Event Details
                    </Typography>
                  </SectionTitle>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.title)}>
                      <FieldLabel>
                        Audit Title <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="audit-title"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        placeholder="e.g., Q2 2025 Branch Audit"
                        fullWidth
                        size="small"
                        error={Boolean(errors.title)}
                        helperText={errors.title || ""}
                        FormHelperTextProps={{
                          sx: {
                            color: "#dc2626",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            margin: 0,
                            marginTop: "4px",
                          },
                        }}
                      />
                    </FormGroup>

                    <FormGroup hasError={Boolean(errors.scheduled_date)}>
                      <FieldLabel>
                        Scheduled Date <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="audit-date"
                        name="scheduled_date"
                        type="date"
                        value={form.scheduled_date}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        inputProps={{
                          min: mode === "create" ? today : undefined,
                        }}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.scheduled_date)}
                        helperText={errors.scheduled_date || ""}
                        FormHelperTextProps={{
                          sx: {
                            color: "#dc2626",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            margin: 0,
                            marginTop: "4px",
                          },
                        }}
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Description / Instructions</FieldLabel>
                      <TextField
                        id="audit-description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        placeholder="Instructions for the auditor, scope of audit, special notes…"
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                      />
                    </FormGroup>
                  </FormRow>

                  {/* Status — READ ONLY IN EDIT MODE */}
                  {mode === "edit" && (
                    <FormRow>
                      <FormGroup sx={{ maxWidth: 320 }}>
                        <FieldLabel>Audit Status</FieldLabel>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            border: "1px solid #e5e7eb",
                            bgcolor: "#f9fafb",
                            minHeight: 40,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: selStatus?.color ?? "#6b7280",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {selStatus?.label ?? "Unknown Status"}
                          </Typography>
                        </Box>
                        <FormHelperText sx={{ ml: 0.5, mt: 0.5 }}>
                          Status is automatically managed by the audit workflow.
                        </FormHelperText>
                      </FormGroup>
                    </FormRow>
                  )}
                </CardContent>
              </SectionCard>

              {/* ===== SECTION 2: LOCATION / BRANCH ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <LocationOnIcon color="primary" fontSize="small" />
                    <Typography
                      sx={{
                        fontSize: "0.9375rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      Location / Branch
                    </Typography>
                  </SectionTitle>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.location_id)}>
                      <FieldLabel>
                        Location <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <FormControl
                        fullWidth
                        size="small"
                        error={Boolean(errors.location_id)}
                      >
                        <Select
                          id="audit-location"
                          name="location_id"
                          value={form.location_id}
                          onChange={handleChange}
                          displayEmpty
                          disabled={loadingMeta}
                        >
                          <MenuItem value="">
                            {loadingMeta
                              ? "Loading locations…"
                              : "Select a location / branch"}
                          </MenuItem>
                          {locations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.locationCode
                                ? `${loc.name} (${loc.locationCode})`
                                : loc.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.location_id && (
                          <ErrorMessage>{errors.location_id}</ErrorMessage>
                        )}
                      </FormControl>
                    </FormGroup>
                  </FormRow>

                  {/* Info / Warning alerts */}
                  {form.location_id && (
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon />}
                      sx={{ borderRadius: 2, py: 0.5, mt: 1 }}
                    >
                      {mode === "edit" && !locationChanged ? (
                        <>
                          Existing audit items at this location will be kept.
                          Only metadata (title, date, auditor) will update.
                        </>
                      ) : (
                        <>
                          On save, all assets at{" "}
                          <strong>
                            {selLocation?.name || "this location"}
                          </strong>{" "}
                          will be loaded and added to the audit as{" "}
                          <strong>Pending</strong>.
                        </>
                      )}
                    </Alert>
                  )}

                  {locationChanged && (
                    <Alert
                      severity="warning"
                      sx={{ borderRadius: 2, py: 0.5, mt: 1 }}
                    >
                      Location changed — existing audit items will be replaced
                      with assets from the new location when you save.
                    </Alert>
                  )}
                </CardContent>
              </SectionCard>

              {/* ===== SECTION 3: AUDITOR ASSIGNMENT ===== */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <PersonIcon color="primary" fontSize="small" />
                    <Typography
                      sx={{
                        fontSize: "0.9375rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      Auditor Assignment
                    </Typography>

                    {/* Lock badge — only shows when audit is COMPLETED */}
                    {auditorIsLocked && (
                      <Chip
                        icon={<LockIcon sx={{ fontSize: "13px !important" }} />}
                        label="Locked"
                        size="small"
                        sx={{
                          ml: "auto",
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#fef3c7",
                          color:   "#92400e",
                          border:  "1px solid #fde68a",
                          "& .MuiChip-icon": { color: "#92400e" },
                        }}
                      />
                    )}

                    {/* Reassignment badge — shows when picking new auditor mid-audit */}
                    {isReassignmentInProgress && (
                      <Chip
                        icon={<SwapHorizIcon sx={{ fontSize: "13px !important" }} />}
                        label="Reassigning"
                        size="small"
                        sx={{
                          ml: "auto",
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#dbeafe",
                          color:   "#1e40af",
                          border:  "1px solid #bfdbfe",
                          "& .MuiChip-icon": { color: "#1e40af" },
                        }}
                      />
                    )}
                  </SectionTitle>

                  {/* Locked banner — only when audit is COMPLETED */}
                  {auditorIsLocked && (
                    <Alert
                      severity="warning"
                      icon={<AdminPanelSettingsIcon />}
                      sx={{ borderRadius: 2, mb: 2 }}
                    >
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Auditor assignment is locked
                      </Typography>
                      <Typography variant="body2">
                        This audit is <strong>completed</strong> and has been
                        finalized. The assigned auditor can no longer be
                        changed. Each asset record permanently stores who
                        audited it.
                      </Typography>
                    </Alert>
                  )}

                  {/* Info banner — shown when reassigning mid-audit */}
                  {isReassignmentInProgress && (
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon />}
                      sx={{ borderRadius: 2, mb: 2 }}
                    >
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Reassigning auditor mid-audit
                      </Typography>
                      <Typography variant="body2">
                        Assets already audited will{" "}
                        <strong>keep their original auditor</strong> on record.
                        The new auditor will only see and be able to update
                        assets still marked as <strong>Pending</strong>.
                      </Typography>
                    </Alert>
                  )}

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.auditor_id)}>
                      <FieldLabel>
                        Assign Auditor <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <FormControl
                        fullWidth
                        size="small"
                        error={Boolean(errors.auditor_id)}
                      >
                        <Select
                          id="audit-auditor"
                          name="auditor_id"
                          value={form.auditor_id}
                          onChange={handleChange}
                          displayEmpty
                          disabled={loadingMeta || auditorIsLocked}
                        >
                          <MenuItem value="">
                            {loadingMeta
                              ? "Loading auditors…"
                              : auditors.length === 0
                                ? "No auditors available"
                                : "Select auditor"}
                          </MenuItem>
                          {auditors.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {resolveUserName(u)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {u.email}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.auditor_id ? (
                          <ErrorMessage>{errors.auditor_id}</ErrorMessage>
                        ) : auditors.length === 0 && !loadingMeta && !auditorIsLocked ? (
                          <ErrorMessage>
                            No auditor-role users found. Create an auditor
                            account first.
                          </ErrorMessage>
                        ) : auditorIsLocked ? (
                          <FormHelperText sx={{ ml: 0, mt: 0.5, color: "#92400e" }}>
                            Auditor is locked — audit is completed.
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    </FormGroup>
                  </FormRow>

                  {/* Selected Auditor Info Card */}
                  {selAuditor && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: auditorIsLocked
                          ? "#fffbeb"
                          : isReassignmentInProgress
                            ? "#f0f9ff"
                            : "#eff6ff",
                        border: `1px solid ${
                          auditorIsLocked
                            ? "#fde68a"
                            : isReassignmentInProgress
                              ? "#93c5fd"
                              : "#bfdbfe"
                        }`,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: auditorIsLocked
                            ? "#d97706"
                            : isReassignmentInProgress
                              ? "#2563eb"
                              : "#1d4ed8",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          flexShrink: 0,
                        }}
                      >
                        {resolveUserName(selAuditor).charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {resolveUserName(selAuditor)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selAuditor.email}
                        </Typography>
                      </Box>

                      {/* Right-side chip: Locked / New Assignment / Auditor */}
                      {auditorIsLocked ? (
                        <Chip
                          icon={<LockIcon sx={{ fontSize: "12px !important" }} />}
                          label="Locked"
                          size="small"
                          sx={{
                            bgcolor: "#fde68a",
                            color:   "#92400e",
                            fontWeight: 600,
                            flexShrink: 0,
                            "& .MuiChip-icon": { color: "#92400e" },
                          }}
                        />
                      ) : isReassignmentInProgress ? (
                        <Chip
                          icon={<SwapHorizIcon sx={{ fontSize: "12px !important" }} />}
                          label="New Assignment"
                          size="small"
                          sx={{
                            bgcolor: "#dbeafe",
                            color:   "#1e40af",
                            fontWeight: 600,
                            flexShrink: 0,
                            "& .MuiChip-icon": { color: "#1e40af" },
                          }}
                        />
                      ) : (
                        <Chip
                          label="Auditor"
                          size="small"
                          sx={{
                            bgcolor: "#bfdbfe",
                            color:   "#1d4ed8",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>

            </Stack>
          </Container>
        </Box>

        {/* ── STICKY FOOTER ACTION BAR ── */}
        <Box
          sx={{
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#fff",
            py: 2,
            px: 3,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Container maxWidth={false} disableGutters>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                type="button"
                onClick={onClose}
                disabled={submitting}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="audit-form"
                disabled={isSubmitDisabled}
                sx={submitBtnSx}
              >
                {submitting && <CircularProgress size={16} color="inherit" />}
                {submitting
                  ? mode === "edit"
                    ? "Saving Changes…"
                    : "Loading Assets…"
                  : mode === "edit"
                    ? "Save Changes"
                    : "Create Audit"}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* ── SNACKBAR ── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError("")}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{
            width: "100%",
            minWidth: 400,
            maxWidth: 600,
            fontSize: "0.9rem",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            alignItems: "center",
          }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateEditAuditModal;